import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Chip, LinearProgress } from "@mui/material";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

import Langs, { Localize } from "../../app/lang/langs";
import styles from "./SuggestedProfiles.module.css";
import SuggestedProfileModal from "./SuggestedProfileModal";
import {
    clearSuggestions, getVReferenceSuggestions
} from "../vreference/vreferenceSlice";

// The competences field is a free-text textarea; re-querying on every keystroke would be one
// request per character. Longer than the directory's own 300ms because this is a background
// aid, not the thing the user is looking at.
const SUGGEST_DEBOUNCE_MS = 600

/**
 * "Who do we already know for this poste?" — shown inside the Opportunity form.
 *
 * Reads from V-Reference, the referral directory. The person filling in an opportunity is
 * usually NOT the person who referred the candidate, so this deliberately crosses a privacy
 * boundary and is gated on `opportunities:edit` — enforced server-side in
 * VReferencesController.suggestions(), which this component does not duplicate. The caller
 * renders it only when that permission is held; the server refuses regardless.
 *
 * What arrives here is a summary: name, poste, company, whether a CV exists. No email, no
 * phone, no notes, and never the HR-only status or review — those need the per-candidate
 * call behind the modal, and the HR fields need V-Reference rights on top.
 */
export default function SuggestedProfilesPanel({ idPoste, competences }) {

    const dispatch = useDispatch()
    const debounceRef = useRef(null)

    const suggestions = useSelector((state) => state.vreference.suggestions)
    const lang = useSelector((state) => state.settings.lang)

    const [isLoading, setIsLoading] = useState(false)
    const [selectedId, setSelectedId] = useState(null)

    const load = useCallback(async (poste, skills) => {
        if (!poste) {
            dispatch(clearSuggestions())
            return
        }
        setIsLoading(true)
        await dispatch(getVReferenceSuggestions({ id_poste: poste, competences: skills || undefined }))
        setIsLoading(false)
    }, [dispatch])

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => load(idPoste, competences), SUGGEST_DEBOUNCE_MS)
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
    }, [idPoste, competences, load])

    // Clearing on unmount matters: the panel is inside a dialog that is reused for the next
    // opportunity, and stale suggestions from the previous one would flash before the new
    // request lands.
    useEffect(() => () => { dispatch(clearSuggestions()) }, [dispatch])

    // No poste chosen yet — say why the panel is empty rather than showing a bare box.
    if (!idPoste) {
        return (
            <div className={styles.panel}>
                <div className={styles.header}>
                    <PersonSearchOutlinedIcon className={styles.headerIcon} fontSize="small" />
                    <span className={styles.title}><Langs str="VREF_SUGGEST_TITLE" /></span>
                </div>
                <div className={styles.hint}><Langs str="VREF_SUGGEST_PICK_POSTE" /></div>
            </div>
        )
    }

    return (
        <div className={styles.panel}>
            <div className={styles.header}>
                <PersonSearchOutlinedIcon className={styles.headerIcon} fontSize="small" />
                <span className={styles.title}><Langs str="VREF_SUGGEST_TITLE" /></span>
                {suggestions.length ? (
                    <span className={styles.count}>{suggestions.length}</span>
                ) : null}
            </div>

            {isLoading
                ? <LinearProgress className={styles.progress} />
                : <div className={styles.progressPlaceholder} />}

            {!isLoading && !suggestions.length ? (
                <div className={styles.hint}><Langs str="VREF_SUGGEST_EMPTY" /></div>
            ) : null}

            {suggestions.length ? (
                <>
                    <div className={styles.hint}>
                        <Langs str={competences ? "VREF_SUGGEST_RANKED" : "VREF_SUGGEST_BY_POSTE"} />
                    </div>
                    <div className={styles.list}>
                        {suggestions.map(candidate => (
                            <button
                                type="button"
                                key={candidate.id}
                                className={styles.card}
                                onClick={() => setSelectedId(candidate.id)}
                            >
                                <div className={styles.cardMain}>
                                    <span className={styles.name}>
                                        {candidate.first_name} {candidate.last_name}
                                    </span>
                                    <span className={styles.meta}>
                                        {candidate.poste_name || candidate.poste_label || '-'}
                                        {candidate.company ? ` · ${candidate.company}` : ''}
                                    </span>
                                    <span className={styles.referrer}>
                                        <Langs str="VREFERENCE_REFERRED_BY" />: {candidate.created_by_name || '-'}
                                    </span>
                                </div>
                                {/* has_cv arrives as 1/0 from MySQL's EXISTS, not a boolean. */}
                                {candidate.has_cv ? (
                                    <Chip
                                        size="small"
                                        className={styles.cvChip}
                                        icon={<DescriptionOutlinedIcon />}
                                        label={Localize("VREF_SUGGEST_HAS_CV", lang)}
                                    />
                                ) : null}
                            </button>
                        ))}
                    </div>
                </>
            ) : null}

            <SuggestedProfileModal
                idVReference={selectedId}
                onHide={() => setSelectedId(null)}
            />
        </div>
    )
}
